import { type FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { USER_ROLE_LABELS } from '../features/admin/users/types';
import { api, ApiError } from '../lib/api-client';

import type { InvitePreviewWire } from '../features/admin/users/types';

type Blocked =
  | { kind: 'not-found' }
  | { kind: 'not-pending'; reason: string }
  | { kind: 'error'; message: string };

export function blockedCopy(b: Blocked): { title: string; hint: string } {
  if (b.kind === 'not-found') {
    return {
      title: 'Este convite não existe.',
      hint: 'Confira se o link veio completo. Se veio, peça um convite novo a quem convidou você.',
    };
  }
  if (b.kind === 'not-pending') {
    if (b.reason === 'expired') {
      return {
        title: 'Este convite expirou.',
        hint: 'Convites valem 7 dias. Peça um novo a quem convidou você.',
      };
    }
    if (b.reason === 'accepted') {
      return {
        title: 'Este convite já foi usado.',
        hint: 'Sua conta já existe — entre com seu e-mail e senha.',
      };
    }
    if (b.reason === 'revoked') {
      return { title: 'Este convite foi cancelado.', hint: 'Fale com quem convidou você.' };
    }
    return {
      title: 'Este convite não está mais disponível.',
      hint: 'Peça um novo a quem convidou você.',
    };
  }
  return { title: 'Não foi possível abrir o convite.', hint: b.message };
}

function toBlocked(err: unknown): Blocked {
  if (err instanceof ApiError) {
    if (err.status === 404) return { kind: 'not-found' };
    if (err.status === 410) {
      const reason = (err.body as { reason?: string }).reason ?? 'unknown';
      return { kind: 'not-pending', reason };
    }
    return { kind: 'error', message: err.message };
  }
  return { kind: 'error', message: err instanceof Error ? err.message : 'Erro inesperado.' };
}

/**
 * Página PÚBLICA de aceite de convite (`/convite/:secret`, 04/Set/2026 — achado A-01).
 * O convidado vê para qual empresa e com qual papel foi convidado, cria nome e senha, e vai
 * para o login. Antes desta página o convite parava no 201: ninguém conseguia entrar.
 */
export function AceitarConvitePage(): JSX.Element {
  const { secret = '' } = useParams<{ secret: string }>();
  const [preview, setPreview] = useState<InvitePreviewWire | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Lume — Aceitar convite';
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .previewInvite(secret)
      .then((p) => {
        if (alive) setPreview(p);
      })
      .catch((err: unknown) => {
        if (alive) setBlocked(toBlocked(err));
      });
    return () => {
      alive = false;
    };
  }, [secret]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (name.trim().length < 2) {
      setError('Diga seu nome (pelo menos 2 letras).');
      return;
    }
    if (password.length < 8) {
      setError('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As duas senhas não coincidem.');
      return;
    }
    setSubmitting(true);
    try {
      await api.acceptInvite(secret, { name: name.trim(), password });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Já existe uma conta com este e-mail. Entre com sua senha.');
      } else if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        setBlocked(toBlocked(err));
      } else {
        setError(err instanceof Error ? err.message : 'Erro inesperado.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg bg-surface p-8 shadow-md border border-border-subtle">
        {blocked !== null && (
          <div className="space-y-3 text-center" role="alert">
            <h1 className="text-xl font-semibold tracking-tight text-text-primary">
              {blockedCopy(blocked).title}
            </h1>
            <p className="text-sm text-text-secondary">{blockedCopy(blocked).hint}</p>
            <Link to="/login" className="btn btn--secondary w-full">
              Ir para o login
            </Link>
          </div>
        )}

        {blocked === null && preview === null && (
          <p className="text-sm text-text-secondary text-center" role="status">
            Abrindo o convite…
          </p>
        )}

        {blocked === null && preview !== null && done && (
          <div className="space-y-3 text-center" role="status">
            <h1 className="text-xl font-semibold tracking-tight text-text-primary">
              Conta criada.
            </h1>
            <p className="text-sm text-text-secondary">
              Entre com <strong>{preview.email}</strong> e a senha que você acabou de criar.
            </p>
            <Link to="/login" className="btn btn--primary w-full">
              Entrar
            </Link>
          </div>
        )}

        {blocked === null && preview !== null && !done && (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-xl font-semibold tracking-tight text-text-primary">
                Você foi convidado(a) para a Lume
              </h1>
              <p className="text-sm text-text-secondary">
                <strong>{preview.tenantName}</strong> ·{' '}
                {USER_ROLE_LABELS[preview.primaryRole] ?? preview.primaryRole}
              </p>
              <p className="text-xs text-text-secondary">{preview.email}</p>
            </div>

            <form className="space-y-4" onSubmit={(e) => void handleSubmit(e)} noValidate>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-text-primary">Seu nome</span>
                <input
                  className="input w-full"
                  aria-label="Seu nome"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-text-primary">Crie uma senha</span>
                <input
                  className="input w-full"
                  aria-label="Crie uma senha"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="text-xs text-text-secondary">Pelo menos 8 caracteres.</span>
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium text-text-primary">Repita a senha</span>
                <input
                  className="input w-full"
                  aria-label="Repita a senha"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </label>
              {error !== null && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
              <button type="submit" className="btn btn--primary w-full" disabled={submitting}>
                {submitting ? 'Criando conta…' : 'Criar minha conta'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
